import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  approveTransaction,
  cancelTransaction,
  completeTransaction,
  createTransaction,
  getTransaction,
  requestRevision,
  sendForApproval,
  updateNickName,
  updateNarration,
  getAssociatePublicProfile,
  verifyAssociate,
  raiseGrievance,
  updateRequiredDocuments,
  type CreateTransactionPayload,
} from '../api/transactions';
import { getTokenStore } from '../api/token-store';
import type { TransactionDetail, Uuid } from '../types/api';

export function transactionKey(id: Uuid) {
  return ['transaction', id] as const;
}

export function useTransaction(id: Uuid | undefined) {
  return useQuery({
    queryKey: id ? transactionKey(id) : ['transaction', 'disabled'],
    queryFn: () => getTransaction(id as Uuid),
    enabled: !!id && !!getTokenStore().getAccessToken(),
    staleTime: 20_000,
  });
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>, id: Uuid) {
  qc.invalidateQueries({ queryKey: transactionKey(id) });
  qc.invalidateQueries({ queryKey: ['transactions'] });
  qc.invalidateQueries({ queryKey: ['chat', id, 'history'] });
}

export function useCreateTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateTransactionPayload) => createTransaction(payload),
    onSuccess: (tx: TransactionDetail) => {
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.setQueryData(transactionKey(tx.id), tx);
    },
  });
}

export function useCancelTransaction(id: Uuid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) => cancelTransaction(id, reason),
    onSuccess: () => invalidateAll(qc, id),
  });
}

export function useApproveTransaction(id: Uuid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => approveTransaction(id),
    onSuccess: () => invalidateAll(qc, id),
  });
}

export function useRequestRevision(id: Uuid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (notes: string) => requestRevision(id, notes),
    onSuccess: () => invalidateAll(qc, id),
  });
}

export function useCompleteTransaction(id: Uuid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload?: { rating?: number; feedbackDescription?: string }) =>
      completeTransaction(id, payload),
    onSuccess: () => invalidateAll(qc, id),
  });
}

export function useSendForApproval(id: Uuid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => sendForApproval(id),
    onSuccess: () => invalidateAll(qc, id),
  });
}

export function useUpdateNickName(id: Uuid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (nickName: string | null) => updateNickName(id, nickName),
    onSuccess: () => invalidateAll(qc, id),
  });
}

export function useUpdateNarration(id: Uuid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (narration: string | null) => updateNarration(id, narration),
    onSuccess: () => invalidateAll(qc, id),
  });
}

export function useAssociatePublicProfile(id: Uuid | undefined, enabled = true) {
  return useQuery({
    queryKey: ['associate-profile', id],
    queryFn: () => getAssociatePublicProfile(id as Uuid),
    enabled: !!id && enabled && !!getTokenStore().getAccessToken(),
    staleTime: 60_000,
  });
}

export function useVerifyAssociate(id: Uuid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { action: 'CONTINUE' | 'REJECT'; reason?: string }) =>
      verifyAssociate(id, payload),
    onSuccess: () => invalidateAll(qc, id),
  });
}

export function useRaiseGrievance(id: Uuid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) => raiseGrievance(id, reason),
    onSuccess: () => invalidateAll(qc, id),
  });
}

export function useUpdateRequiredDocuments(id: Uuid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (docs: string[]) => updateRequiredDocuments(id, docs),
    onSuccess: () => invalidateAll(qc, id),
  });
}
